import { CampaignHeroSlice } from '@adapters/prismic/types/brandedStorePage';
import mapCampaignHeroData from '@adapters/prismic/utils/mapCampaignHeroData';

/**
 * Which edges of a CIP/CLP slice paint edge to edge — a full-width image or
 * coloured band rather than content on the page's white background.
 *
 * The slice zones (`CategoryPageSliceZone`, `LandingPageSliceZone`,
 * `HomepageSliceZone`) put one consistent margin after every slice, which
 * reads well between white content but leaves an odd white stripe between two
 * full-bleed slices (e.g. a hero straight into a grey promo band). They render
 * these flags as `data-bleed-top` / `data-bleed-bottom` on each slice wrapper
 * and drop the margin when one slice's bottom and the next slice's top both
 * bleed, so the two sit flush — matching Figma, where full-bleed components butt together.
 *
 * Grey bands are also flagged (`data-grey-band`): two in a row merge into
 * one grey block, so the zones swap their stacked padding (first's bottom +
 * second's top, double the usual gap) for a single 80px between contents.
 *
 * The zones only wrap slices that `isSliceRendered` passes, so these
 * adjacency rules compare visible slices, never a flagged-off or empty one.
 *
 * Covers every slice type used on CIPs/CLPs. An audit of every published EN
 * page (Oct 2026, at 390px and 1440px) found the other legacy types are all
 * contained — none paints edge to edge at its top or bottom — so they keep
 * the margin. Re-check a new or restyled slice type before adding it here.
 */
export interface SliceBleed {
  top: boolean;
  bottom: boolean;
}

const BOTH: SliceBleed = { top: true, bottom: true };
const NONE: SliceBleed = { top: false, bottom: false };

/** Full-bleed image (`main_hero`) or grey band (`promo_tiles`,
    `learn_strip`) on both edges. */
const FULL_BLEED_SLICES = new Set(['main_hero', 'promo_tiles', 'learn_strip']);

/** Full-bleed slices whose band is the same grey (`--neutrals-100`). */
const GREY_BAND_SLICES = new Set(['promo_tiles', 'learn_strip']);

// `primary` is left `unknown`: this takes every slice type in the zones'
// unions, whose `primary` shapes share no fields.
export const getSliceBleed = (
  slice?: { slice_type?: string; primary?: unknown } | null
): SliceBleed => {
  if (!slice?.slice_type) return NONE;
  if (FULL_BLEED_SLICES.has(slice.slice_type)) return BOTH;

  // The image always runs to the bottom edge, but the optional heading sits
  // above it on white — only the top bleeds when there's no heading.
  if (
    slice.slice_type === 'image_hotspot' ||
    slice.slice_type === 'image_product_strip'
  ) {
    const { heading } = (slice.primary ?? {}) as { heading?: unknown };
    const hasHeading = typeof heading === 'string' && heading.trim() !== '';
    return { top: !hasHeading, bottom: true };
  }

  // Legacy: full-width image on top, but its heading/body copy always
  // renders below it on white.
  if (slice.slice_type === 'hero_banner') return { top: true, bottom: false };

  // Copy on white above the image, which runs to the bottom edge. Side by
  // side stacks the same way on mobile, and these flags aren't responsive.
  if (slice.slice_type === 'campaign_hero') {
    const { image } = mapCampaignHeroData(
      (slice.primary ?? {}) as CampaignHeroSlice['primary']
    );
    return { top: false, bottom: !!image };
  }

  return NONE;
};

/** Spread onto a slice wrapper — attributes are omitted, not `"false"`, so
    the CSS can match on presence alone. */
export const sliceBleedAttributes = (
  slice: Parameters<typeof getSliceBleed>[0]
): {
  'data-bleed-top'?: true;
  'data-bleed-bottom'?: true;
  'data-grey-band'?: true;
} => {
  const { top, bottom } = getSliceBleed(slice);
  return {
    ...(top ? { 'data-bleed-top': true } : {}),
    ...(bottom ? { 'data-bleed-bottom': true } : {}),
    ...(slice?.slice_type && GREY_BAND_SLICES.has(slice.slice_type)
      ? { 'data-grey-band': true }
      : {}),
  };
};
